import { APP_SIGN_UP_URL } from "@/lib/appLinks";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => (
  <section className="dot-texture relative overflow-hidden pt-28 pb-14 sm:pt-36 sm:pb-16">
    <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="inline-flex items-center gap-2 rounded-full border border-guinep-line bg-guinep-soft px-4 py-1.5"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-guinep-mid" />
        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-guinep-mid">Guinep Seed</span>
      </motion.div>

      <h1 className="mt-7 font-serif text-[2.75rem] font-medium leading-[1.06] tracking-tight text-guinep-ink sm:text-6xl">
        {["From social discovery", "to client action."].map((l, i) => (
          <span key={l} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
            <motion.span className="block" custom={i} variants={line} initial="hidden" animate="show">
              {i === 1 ? (
                <>
                  to <em className="font-medium italic text-guinep-mid">client action.</em>
                </>
              ) : (
                l
              )}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-guinep-mute sm:text-lg"
      >
        Your simple business page for services, availability, policies and enquiries — made for
        businesses that get clients through social media.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
      >
        <a
          href={APP_SIGN_UP_URL}
          data-testid="hero-get-started-button"
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-guinep-deep px-7 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-guinep-mid hover:shadow-[0_12px_32px_-10px_rgba(31,58,43,0.55)] sm:w-auto"
        >
          Create your Guinep Seed page
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
        <button
          data-testid="hero-see-how-it-works-button"
          onClick={() => scrollToId("how-it-works")}
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-guinep-line px-7 py-3.5 text-base font-semibold text-guinep-ink transition-colors duration-300 hover:border-guinep-mid hover:bg-guinep-soft sm:w-auto"
        >
          See how it works
          <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
        </button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.05 }}
        className="mt-5 text-xs text-guinep-mute/80 sm:text-sm"
      >
        Share it in your bio, WhatsApp, QR code or social posts.
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.2 }}
        className="mt-4"
      >
        <Link
          to="/demo"
          data-testid="hero-demo-link"
          className="group inline-flex items-center gap-1.5 text-sm font-semibold text-guinep-mid transition-colors duration-300 hover:text-guinep-deep"
        >
          Try Guinep Seed as your customer would
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </Link>
      </motion.div>
    </div>
  </section>
);

export default Hero;
